import { useState, useEffect } from 'react';
import { useFormik } from 'formik';
import * as yup from 'yup';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useHistory } from 'react-router-dom';
import {
  BenefitTypeEnum,
  ItemStatusEnum,
} from '../../../api/payments/items/domain/enum';
import { BenefitTypeLabels } from '../../../api/payments/items/domain/labels';
import { normalizeBenefitType, normalizeStatus } from '../utils';
import { ItemsAPI } from '../../../api/payments/items/api';
import { config } from '../../../../config/envs';

const INITIAL_VALUES = {
  name: '',
  code: '',
  value: 0.0,
  saleType: 'ADD_ON',
  statusType: normalizeStatus(),
  benefitType: normalizeBenefitType(),
  display_client: false,
};

const validationSchema = yup.object().shape({
  name: yup.string().required('O campo nome é obrigatório!'),
  code: yup
    .string()
    .required('O campo código do produto é obrigatório!'),
  value: yup.number().required('O campo valor é obrigatório!'),
  saleType: yup.string().required('O campo tipo de venda é obrigatório!'),
  benefitType: yup
    .string()
    .required('O campo tipo de benefício é obrigatório!'),
});

const STATUS_TYPE_LOOKUP = {
  [ItemStatusEnum.ACTIVE]: {
    value: ItemStatusEnum.ACTIVE,
    label: 'Ativo',
  },
  [ItemStatusEnum.INACTIVE]: {
    value: ItemStatusEnum.INACTIVE,
    label: 'Desativado',
  },
};

const BENEFIT_TYPE_LOOKUP = {
  [BenefitTypeEnum.ACCESS]: {
    value: BenefitTypeEnum.ACCESS,
    label: BenefitTypeLabels[BenefitTypeEnum.ACCESS],
  },
  [BenefitTypeEnum.CREDITS]: {
    value: BenefitTypeEnum.CREDITS,
    label: BenefitTypeLabels[BenefitTypeEnum.CREDITS],
  },
};

const formatToCurrency = (value) => {
  const number = Number.parseFloat(value) || 0;

  return number.toLocaleString('pt-BR', {
    style: 'currency',
    currency: 'BRL',
  });
};

const parseBoolean = (value) => {
  if (typeof value === 'boolean') return value;
  if (typeof value === 'number') return value === 1;
  if (typeof value === 'string')
    return value.toLowerCase() === 'true' || value === '1';
  return Boolean(value);
};

const parseCurrencyInput = (value) => {
  const cleanValue = String(value)
    .replace(/[^\d,]/g, '')
    .replace(/\./g, '')
    .replace(',', '.');

  return Number.parseFloat(cleanValue);
};

const sanitizeCurrencyInput = (value) => {
  let sanitizedValue = String(value).replace(/[^\d,]/g, '');
  const firstCommaIndex = sanitizedValue.indexOf(',');

  if (firstCommaIndex !== -1) {
    const integerPart = sanitizedValue.slice(0, firstCommaIndex);
    const decimalPart = sanitizedValue
      .slice(firstCommaIndex + 1)
      .replace(/,/g, '')
      .slice(0, 2);

    return `${integerPart},${decimalPart}`;
  }

  return sanitizedValue;
};

export function useAddItemBootstrap(itemId) {
  const history = useHistory();
  const [initialValues, setInitialValues] = useState(INITIAL_VALUES);
  const [loading, setLoading] = useState(true);
  const [displayValue, setDisplayValue] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);

  useEffect(() => {
    const fetchInitialValues = async () => {
      if (itemId) {
        try {
          const response = await ItemsAPI.get(itemId);
          const data = response.data;
          setInitialValues({
            ...INITIAL_VALUES,
            ...data,
            benefitType: normalizeBenefitType(
              data?.benefitType || data?.benefits_type,
            ),
            display_client: Boolean(data.display_client),
          });
        } catch (error) {
          console.error('Erro ao buscar os dados do plano', error);
          toast.error('Erro ao carregar dados iniciais.');
        }
      }
      setLoading(false);
    };

    fetchInitialValues();
  }, [itemId]);

  const formik = useFormik({
    enableReinitialize: true,
    initialValues,
    validationSchema,
    onSubmit: async (values, { setSubmitting }) => {
      setSubmitting(true);
      try {
        if (itemId) {
          await ItemsAPI.update(itemId, values);
        } else {
          await ItemsAPI.create(values);
        }

        toast.success('Produto salvo com sucesso!');
        history.push('/payments/items');
      } catch (error) {
        const message =
          error?.response?.data?.message ||
          'Ops... tivemos um problema de conexão por isso não conseguimos salvar seu produto.';
        toast.error(message);
        console.error(error);
      } finally {
        setSubmitting(false);
      }
    },
  });

  useEffect(() => {
    const currentValue = formik.values.value;

    if (
      currentValue !== undefined &&
      currentValue !== null &&
      currentValue !== '' &&
      !Number.isNaN(Number(currentValue))
    ) {
      setDisplayValue(formatToCurrency(currentValue));
      return;
    }

    setDisplayValue('');
  }, [formik.values.value]);

  const handleValueChange = (event) => {
    setDisplayValue(sanitizeCurrencyInput(event.target.value));
  };

  const handleValueFocus = () => {
    const currentValue = formik.values.value;

    if (
      currentValue === undefined ||
      currentValue === null ||
      currentValue === '' ||
      Number.isNaN(Number(currentValue))
    ) {
      setDisplayValue('');
      return;
    }

    const numericValue = Number(currentValue);

    if (numericValue === 0) {
      setDisplayValue('');
      return;
    }

    setDisplayValue(
      numericValue.toLocaleString('pt-BR', {
        minimumFractionDigits: 2,
        maximumFractionDigits: 2,
      }),
    );
  };

  const handleValueBlur = () => {
    const sanitizedValue = sanitizeCurrencyInput(displayValue);
    const numericValue = parseCurrencyInput(sanitizedValue);

    if (Number.isNaN(numericValue)) {
      setDisplayValue('');
      formik.setFieldValue('value', '');
      formik.setFieldTouched('value', true);
      return;
    }

    setDisplayValue(formatToCurrency(numericValue));
    formik.setFieldValue('value', numericValue);
    formik.setFieldTouched('value', true);
  };

  const handleDeleteItem = async () => {
    try {
      setIsDeleting(true);

      await axios.delete(`${config.app.adminBaseUrl}/items/${itemId}`);

      toast.success('Produto excluído com sucesso.');
      history.push('/payments/items');
    } catch (error) {
      console.error('Erro ao excluir produto', error);
      toast.error('Não foi possível excluir o produto. Tente novamente.');
    } finally {
      setIsDeleting(false);
      setShowDeleteModal(false);
    }
  };

  const handleCancel = () => history.push('/payments/items');

  const selectedStatusType =
    STATUS_TYPE_LOOKUP[normalizeStatus(formik.values.statusType)] || null;

  const selectedBenefitsType =
    BENEFIT_TYPE_LOOKUP[normalizeBenefitType(formik.values.benefitType)] ||
    null;

  return {
    itemId,
    loading,
    formik,
    displayValue,
    showDeleteModal,
    setShowDeleteModal,
    isDeleting,
    statusTypeOptions: Object.values(STATUS_TYPE_LOOKUP),
    benefitsTypeOptions: Object.values(BENEFIT_TYPE_LOOKUP),
    selectedStatusType,
    selectedBenefitsType,
    handleValueChange,
    handleValueFocus,
    handleValueBlur,
    handleDeleteItem,
    handleCancel,
    parseBoolean,
  };
}
